<script setup lang="ts">
import type { FilterDropdownProps } from '.';

import { cn } from '@/utils';
import { useId } from 'vue';

import { FilterButton } from '~/components/Filters/FilterButton';

import { filterDropdownContentVariants } from './variants';

defineOptions({
	inheritAttrs: false
});

const props = withDefaults(defineProps<FilterDropdownProps>(), {
	label: '',
	numberOfAppliedFilters: 0,
	showNumberOfAppliedFilters: true,
	filterButtonVariant: 'primary',
	contentVariant: 'primary'
});

const attrs = useAttrs();

// Separa o data-test dos demais atributos
const attrsWithoutDataTest = computed(() => {
	const { 'data-test': _, ...rest } = attrs;
	return rest;
});

const dataTestAttr = computed(() => {
	return attrs['data-test'] ? { 'data-test': attrs['data-test'] } : {};
});

const isActive = shallowRef<boolean>(false);
const filterButtonId = useId();
let focusAfterClose: (() => void) | undefined;

const emit = defineEmits<{
	(e: 'close' | 'open'): void;
}>();

watch(isActive, isOpen => {
	const event = isOpen ? 'open' : 'close';
	emit(event);
});

function getTriggerElement() {
	const element = document.getElementById(filterButtonId);
	return element instanceof HTMLButtonElement ? element : null;
}

function focusTrigger() {
	getTriggerElement()?.focus();
}

function focusTriggerIfVisible() {
	const element = getTriggerElement();

	if (!element || element.getClientRects().length === 0) return false;

	element.focus();
	return true;
}

function handleCloseAutoFocus(event: Event) {
	if (!focusAfterClose) return;

	event.preventDefault();
	const focusTarget = focusAfterClose;
	focusAfterClose = undefined;
	nextTick(focusTarget);
}

defineExpose({
	close: (options?: { focusAfterClose?: () => void }) => {
		focusAfterClose = options?.focusAfterClose ?? focusTrigger;
		isActive.value = false;
	},
	focusTrigger,
	focusTriggerIfVisible
});
</script>

<template>
	<DropdownMenu v-model:open="isActive">
		<DropdownMenuTrigger
			v-bind="attrsWithoutDataTest"
			class="relative flex size-min"
			:class="cn('', props.class)"
		>
			<FilterButton
				:id="filterButtonId"
				v-bind="dataTestAttr"
				:is-active="isActive"
				:number-of-applied-filters="numberOfAppliedFilters"
				:show-number-of-applied-filters="showNumberOfAppliedFilters"
				:variant="filterButtonVariant"
				:label="label"
				:class="cn('', props.class)"
			>
				<template #label>
					<slot name="label" />
				</template>
				<template #icon>
					<IconMaterialSymbolsLightExpandMore
						class="size-6 shrink-0 rotate-0 text-beige transition-transform duration-200"
						:class="{ 'rotate-180': isActive }"
					/>
				</template>
			</FilterButton>
		</DropdownMenuTrigger>

		<DropdownMenuContent
			align="start"
			prioritize-position
			:class="cn(filterDropdownContentVariants({ variant: contentVariant }))"
			@close-auto-focus="handleCloseAutoFocus"
		>
			<slot name="content" />
		</DropdownMenuContent>
	</DropdownMenu>
</template>
